<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { loadYouTubeApi } from "../../utils/youtube";
import { useOnboardingVideo } from "../../hooks/useOnboardingVideo";

const props = defineProps<{
    videoId: string;
    posterUrl: string | null;
}>();

const emit = defineEmits<{
    play: [];
}>();

const { savePlaybackTime, getPlaybackTime } = useOnboardingVideo();

const showVideo = ref(false);
const playerEl = ref<HTMLDivElement | null>(null);
let player: YT.Player | null = null;
let timeInterval: ReturnType<typeof setInterval> | null = null;

function startTracking() {
    stopTracking();
    timeInterval = setInterval(() => {
        if (player?.getPlayerState?.() === YT.PlayerState.PLAYING) {
            savePlaybackTime(props.videoId, player.getCurrentTime());
        }
    }, 2000);
}

function stopTracking() {
    if (timeInterval) {
        clearInterval(timeInterval);
        timeInterval = null;
    }
}

async function play() {
    showVideo.value = true;
    emit("play");

    await loadYouTubeApi();

    if (!playerEl.value) return;

    const startAt = getPlaybackTime(props.videoId);

    player = new YT.Player(playerEl.value, {
        videoId: props.videoId,
        playerVars: {
            autoplay: 1,
            rel: 0,
            start: startAt,
            modestbranding: 1,
        },
        events: {
            onReady: () => startTracking(),
            onStateChange: (event: YT.OnStateChangeEvent) => {
                if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                    if (player?.getCurrentTime) {
                        savePlaybackTime(props.videoId, player.getCurrentTime());
                    }
                }
            },
        },
    });
}

watch(
    () => props.videoId,
    () => {
        showVideo.value = false;
        stopTracking();
        player?.destroy();
        player = null;
    },
);

onMounted(() => {
    const startAt = getPlaybackTime(props.videoId);
    if (startAt > 0) {
        play();
    }
});

onBeforeUnmount(() => {
    if (player?.getPlayerState?.() === YT.PlayerState.PLAYING) {
        savePlaybackTime(props.videoId, player.getCurrentTime());
    }
    stopTracking();
    player?.destroy();
    player = null;
});
</script>

<template>
    <div class="w-full aspect-video bg-gray-500 relative overflow-hidden">
        <!-- Poster mode -->
        <template v-if="!showVideo">
            <img v-if="posterUrl" :src="posterUrl" alt="" class="w-full h-full object-cover" />
            <button class="absolute inset-0 flex items-center justify-center group" @click="play">
                <div
                    class="flex items-center justify-center h-16 w-16 rounded-full bg-black/50 group-hover:bg-black/70 transition-colors"
                >
                    <span class="material-symbols-outlined text-white text-4xl">play_arrow</span>
                </div>
            </button>
        </template>

        <!-- Video mode -->
        <div v-else ref="playerEl" class="w-full h-full" />
    </div>
</template>
